import { EstimateTemplateRecord } from "common/records/EstimateTemplate";
import { RootState } from "common/reducers";
import { ids, loading } from "common/selectors/idsManagement.selectors";
import { Nullable } from "common/types";
import { createSelector } from "reselect";
import { List, Map } from "immutable";

const model = "estimateTemplates";

// Hoisted so the same object identity is passed to `loading` on every call — `loading` is a
// createSelector, and fresh props per invocation would defeat its memoization.
const LOADING_PROPS = { modelName: model } as const;

/**
 * Loads the byId map.
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateTemplateRecord>} the byId map
 */
export const byId = (state: RootState): Map<number, EstimateTemplateRecord> => state.getIn([model, "byId"]);

/**
 * Loads a single estimate template by id.
 *
 * @param {RootState} state The RootState
 * @param {{ id: number }} props
 * @returns {Nullable<EstimateTemplateRecord>} the record, or undefined
 */
export const estimateTemplate = (state: RootState, props: { id: number }): Nullable<EstimateTemplateRecord> =>
  byId(state).get(props.id);

/**
 * The ordered id list of the currently-listed estimate templates (IdsManagement model).
 *
 * @param {RootState} state The RootState
 * @returns {List<number>} the ordered ids
 */
export const estimateTemplateIds = (state: RootState): List<number> => ids(state, { modelName: model }) as List<number>;

/**
 * Loading flag for the `estimateTemplates` list fetch. Named selector so callers don't reinvent the
 * `loading(state, { modelName })` closure inline.
 *
 * @param {RootState} state The RootState
 * @returns {boolean} whether the template list is currently fetching
 */
export const estimateTemplatesLoading = createSelector(
  [(state: RootState) => loading(state, LOADING_PROPS)],
  (isLoading): boolean => Boolean(isLoading),
);
